import { useState, useEffect, useCallback } from 'react';
import { backendService } from '../api/backendService';
import { usePostgresUser } from '../../components/utils/PostgresUserContext';
import { getToken } from '../../config/firebase';
import { toast } from 'react-toastify';

/**
 * Custom hook for managing user leaderboard messages (Elden Ring style)
 * Provides CRUD operations and state management for user messages
 */
export const useLeaderboardMessage = () => {
  const { user } = usePostgresUser();
  const [userMessage, setUserMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Helper function to get ID token (consistent with other hooks)
  const getIdToken = useCallback(() => {
    const token = getToken();
    if (!token) {
      throw new Error('User not authenticated');
    }
    return token;
  }, []);

  /**
   * Fetch user's current leaderboard message
   */
  const fetchUserMessage = useCallback(async () => {
    if (!user) {
      setUserMessage(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const idToken = await getIdToken();
      const response = await backendService.getUserLeaderboardMessage(idToken);
      
      if (response.success) {
        setUserMessage(response.data);
      } else {
        setError('Failed to fetch message');
      }
    } catch (err) {
      console.error('Error fetching user leaderboard message:', err);
      setError(err.message || 'Failed to fetch message');
      setUserMessage(null);
    } finally {
      setLoading(false);
    }
  }, [user?.uid, getIdToken]); // Only depend on user.uid, not the entire user object

  /**
   * Create or update user's leaderboard message
   * @param {string} message - The message content (max 30 chars)
   * @param {string} messageType - Type of message (default: 'leaderboard')
   * @returns {Promise<boolean>} Success status
   */
  const createOrUpdateMessage = useCallback(async (message, messageType = 'leaderboard') => {
    if (!user) {
      throw new Error('User must be logged in to create messages');
    }

    if (!message || message.trim().length === 0) {
      throw new Error('Message cannot be empty');
    }

    if (message.length > 30) {
      throw new Error('Message must be 30 characters or less');
    }

    try {
      setLoading(true);
      setError(null);
      
      const idToken = await getIdToken();
      const response = await backendService.createLeaderboardMessage(
        message.trim(), 
        messageType, 
        idToken
      );
      
      if (response.success) {
        setUserMessage(response.data);
        toast.success('Leaderboard message saved! 📜');
        return true;
      } else {
        throw new Error(response.error || 'Failed to save message');
      }
    } catch (err) {
      console.error('Error creating/updating leaderboard message:', err);
      setError(err.message || 'Failed to save message');
      toast.error(`Failed to save message: ${err.message}`);
      return false;
    } finally {
      setLoading(false);
    }
  }, [user?.uid, getIdToken]);

  /**
   * Update specific fields of user's existing message
   * @param {Object} updates - Fields to update
   * @returns {Promise<boolean>} Success status
   */
  const updateMessage = useCallback(async (updates) => {
    if (!user) {
      throw new Error('User must be logged in to update messages');
    }

    if (!userMessage) {
      throw new Error('No message exists to update');
    }

    try {
      setLoading(true);
      setError(null);
      
      const idToken = await getIdToken();
      const response = await backendService.updateLeaderboardMessage(updates, idToken);
      
      if (response.success) {
        setUserMessage(response.data);
        toast.success('Message updated! ✨');
        return true;
      } else {
        throw new Error(response.error || 'Failed to update message');
      }
    } catch (err) {
      console.error('Error updating leaderboard message:', err);
      setError(err.message || 'Failed to update message');
      toast.error(`Failed to update message: ${err.message}`);
      return false;
    } finally {
      setLoading(false);
    }
  }, [user?.uid, userMessage?.id, getIdToken]);

  /**
   * Delete user's leaderboard message
   * @returns {Promise<boolean>} Success status
   */
  const deleteMessage = useCallback(async () => {
    if (!user) {
      throw new Error('User must be logged in to delete messages');
    }

    if (!userMessage) {
      throw new Error('No message exists to delete');
    }

    try {
      setLoading(true);
      setError(null);
      
      const idToken = await getIdToken();
      const response = await backendService.deleteLeaderboardMessage(idToken);
      
      if (response.success) {
        setUserMessage(null);
        toast.success('Message deleted 🗑️');
        return true;
      } else {
        throw new Error(response.error || 'Failed to delete message');
      }
    } catch (err) {
      console.error('Error deleting leaderboard message:', err);
      setError(err.message || 'Failed to delete message');
      toast.error(`Failed to delete message: ${err.message}`);
      return false;
    } finally {
      setLoading(false);
    }
  }, [user?.uid, userMessage?.id, getIdToken]);

  /**
   * Check if user has a valid public username for creating messages
   * @returns {boolean} Whether user can create messages
   */
  const canCreateMessage = useCallback(() => {
    if (!user || !user.publicUsername) {
      return false;
    }
    return user.publicUsername.trim().length > 0;
  }, [user?.publicUsername]);

  /**
   * Get user's public username
   * @returns {string|null} Public username or null
   */
  const getPublicUsername = useCallback(() => {
    return user?.publicUsername || null;
  }, [user?.publicUsername]);

  /**
   * Check if user appears on any leaderboard
   * Used to determine if we should show the message creation option
   * @param {Object} leaderboards - All leaderboard data from fetchAllLeaderboards
   * @returns {boolean} Whether user appears on any leaderboard
   */
  const isUserOnLeaderboards = useCallback((leaderboards) => {
    if (!user?.publicUsername || !leaderboards) {
      return false;
    }

    const username = user.publicUsername;
    
    // Check each leaderboard type
    const leaderboardTypes = ['speedRun', 'totalAccuracy', 'streak']; // Updated for PostgreSQL backend
    
    return leaderboardTypes.some(type => {
      const leaderboard = leaderboards[type];
      return leaderboard && leaderboard.some(entry => entry.publicUsername === username);
    });
  }, [user?.publicUsername]);

  // Auto-fetch user message when user changes
  useEffect(() => {
    fetchUserMessage();
  }, [fetchUserMessage]);

  return {
    // State
    userMessage,
    loading,
    error,
    
    // Actions
    createOrUpdateMessage,
    updateMessage,
    deleteMessage,
    fetchUserMessage,
    
    // Utilities
    canCreateMessage,
    getPublicUsername,
    isUserOnLeaderboards,
    
    // Computed values
    hasMessage: !!userMessage,
    messageText: userMessage?.message || null,
    messageType: userMessage?.messageType || 'leaderboard',
    isActive: userMessage?.isActive ?? false
  };
};